import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Plus, 
  Trash2, 
  Facebook, 
  Instagram, 
  Twitter, 
  Linkedin, 
  Youtube,
  Palette,
  Link,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const Footer = () => {
  const [footerData, setFooterData] = useState({
    company_name: '',
    company_description: '',
    contact_phone: '',
    contact_email: '',
    contact_address: '',
    business_hours: '',
    social_media: {
      facebook: '',
      instagram: '',
      linkedin: '',
      twitter: '',
      youtube: ''
    },
    quick_links: [],
    customer_service_links: [],
    footer_links: [],
    copyright_text: '',
    background_color: '#1a1a1a',
    text_color: '#fff',
    accent_color: '#F7C1CC'
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newQuickLink, setNewQuickLink] = useState({ name: '', url: '' });
  const [newCustomerServiceLink, setNewCustomerServiceLink] = useState({ name: '', url: '' });
  const [newFooterLink, setNewFooterLink] = useState({ name: '', url: '' });
  
  // Force refresh to clear any cached references

  const API_BASE_URL = 'https://petifyshop.mycartly.in';

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const loadFooterSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/footer-settings`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setFooterData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load footer settings');
      }
    } catch (err) {
      setError('Failed to load footer settings');
    } finally {
      setLoading(false);
    }
  };

  const saveFooterSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/footer-settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(footerData)
      });

      if (response.ok) {
        setSuccess('Footer settings saved successfully!');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save footer settings');
      }
    } catch (err) {
      setError('Failed to save footer settings');
    } finally {
      setSaving(false);
    }
  };

  const addLink = (linkType, linkData) => {
    if (linkData.name && linkData.url) {
      setFooterData(prev => ({
        ...prev,
        [linkType]: [...prev[linkType], { ...linkData }]
      }));
      // Clear the appropriate input based on link type
      if (linkType === 'quick_links') {
        setNewQuickLink({ name: '', url: '' });
      } else if (linkType === 'customer_service_links') {
        setNewCustomerServiceLink({ name: '', url: '' });
      } else if (linkType === 'footer_links') {
        setNewFooterLink({ name: '', url: '' });
      }
    }
  };

  const removeLink = (type, index) => {
    setFooterData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const updateSocialMedia = (platform, value) => {
    setFooterData(prev => ({
      ...prev,
      social_media: {
        ...prev.social_media,
        [platform]: value
      }
    }));
  };

  useEffect(() => {
    loadFooterSettings();
  }, []);

  const socialMediaIcons = {
    facebook: <Facebook className="w-4 h-4" />,
    instagram: <Instagram className="w-4 h-4" />,
    twitter: <Twitter className="w-4 h-4" />,
    linkedin: <Linkedin className="w-4 h-4" />,
    youtube: <Youtube className="w-4 h-4" />
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading footer settings...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Footer Management</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Information */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Company Information
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input
                type="text"
                value={footerData.company_name}
                onChange={(e) => setFooterData(prev => ({ ...prev, company_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Description</label>
              <textarea
                rows={3}
                value={footerData.company_description}
                onChange={(e) => setFooterData(prev => ({ ...prev, company_description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
              <input
                type="text"
                value={footerData.contact_phone}
                onChange={(e) => setFooterData(prev => ({ ...prev, contact_phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
              <input
                type="email"
                value={footerData.contact_email}
                onChange={(e) => setFooterData(prev => ({ ...prev, contact_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Address</label>
              <textarea
                rows={2}
                value={footerData.contact_address}
                onChange={(e) => setFooterData(prev => ({ ...prev, contact_address: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Hours</label>
              <input
                type="text"
                value={footerData.business_hours}
                onChange={(e) => setFooterData(prev => ({ ...prev, business_hours: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Copyright Text</label>
              <input
                type="text"
                value={footerData.copyright_text}
                onChange={(e) => setFooterData(prev => ({ ...prev, copyright_text: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Colors & Styling */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Colors & Styling
          </h2>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
              <input
                type="color"
                value={footerData.background_color}
                onChange={(e) => setFooterData(prev => ({ ...prev, background_color: e.target.value }))}
                className="w-full h-10 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
              <input
                type="color"
                value={footerData.text_color}
                onChange={(e) => setFooterData(prev => ({ ...prev, text_color: e.target.value }))}
                className="w-full h-10 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Accent Color</label>
              <input
                type="color"
                value={footerData.accent_color}
                onChange={(e) => setFooterData(prev => ({ ...prev, accent_color: e.target.value }))}
                className="w-full h-10 border border-gray-300 rounded-md"
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-lg shadow-md p-6 lg:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Facebook className="w-5 h-5" />
            Social Media Links
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.keys(footerData.social_media).map(platform => (
              <div key={platform} className="flex items-center gap-2">
                {socialMediaIcons[platform]}
                <input
                  type="url"
                  placeholder={`${platform.charAt(0).toUpperCase() + platform.slice(1)} URL`}
                  value={footerData.social_media[platform]}
                  onChange={(e) => updateSocialMedia(platform, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Link className="w-5 h-5" />
            Quick Links
          </h2>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Link Name"
                value={newQuickLink.name}
                onChange={(e) => setNewQuickLink(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="url"
                placeholder="Link URL"
                value={newQuickLink.url}
                onChange={(e) => setNewQuickLink(prev => ({ ...prev, url: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => addLink('quick_links', newQuickLink)}
                disabled={!newQuickLink.name || !newQuickLink.url}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Add Quick Link
              </button>
            </div>
            
            <div className="space-y-2">
              {footerData.quick_links.map((link, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                  <span className="text-sm">{link.name} ({link.url})</span>
                  <button
                    onClick={() => removeLink('quick_links', index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Customer Service Links */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Link className="w-5 h-5" />
            Customer Service Links
          </h2>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Link Name"
                value={newCustomerServiceLink.name}
                onChange={(e) => setNewCustomerServiceLink(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="url"
                placeholder="Link URL"
                value={newCustomerServiceLink.url}
                onChange={(e) => setNewCustomerServiceLink(prev => ({ ...prev, url: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => addLink('customer_service_links', newCustomerServiceLink)}
                disabled={!newCustomerServiceLink.name || !newCustomerServiceLink.url}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Add Customer Service Link
              </button>
            </div>
            
            <div className="space-y-2">
              {footerData.customer_service_links.map((link, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                  <span className="text-sm">{link.name} ({link.url})</span>
                  <button
                    onClick={() => removeLink('customer_service_links', index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Link className="w-5 h-5" />
            Footer Links
          </h2>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Link Name"
                value={newFooterLink.name}
                onChange={(e) => setNewFooterLink(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="url"
                placeholder="Link URL"
                value={newFooterLink.url}
                onChange={(e) => setNewFooterLink(prev => ({ ...prev, url: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => addLink('footer_links', newFooterLink)}
                disabled={!newFooterLink.name || !newFooterLink.url}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Add Footer Link
              </button>
            </div>
            
            <div className="space-y-2">
              {footerData.footer_links.map((link, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                  <span className="text-sm">{link.name} ({link.url})</span>
                  <button
                    onClick={() => removeLink('footer_links', index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-center mt-8">
        <button
          onClick={saveFooterSettings}
          disabled={saving}
          className="flex items-center gap-2 px-8 py-3 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-lg"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Saving...' : 'Save Footer Settings'}
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-md shadow-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-6 py-3 rounded-md shadow-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
    </div>
  );
};

export default Footer;